import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs as importedSaveAs } from 'file-saver';
import * as moment from 'moment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IncidentResponse } from '../../_models/incidentResponse';
import {
  AlertService,
  AuthenticationService, ClientProfileService,
  FileBlobService,
  UserService,
} from '../../_services';
import { IncidentResponseService } from '../../_services/incidentResponse.service';
import { LoginHelper } from '../../helpers';
import { EditIncidentResponseComponent } from '../editIncidentResponse/editIncidentResponse.component';
import {forkJoin} from "rxjs";

@Component({
    standalone: false,
  selector: 'incident-response',
  templateUrl: 'incidentResponse.component.html',
  styleUrls: ['./incidentResponse.component.scss'],
  providers: [DialogService],
})
export class IncidentResponseComponent implements OnInit {
  pageTitle: string;
  incidentResponses: IncidentResponse[];
  cols: any[] = [
    { 'field': 'incidentResponseNumber', 'header': 'Incident Response Number' },
    { 'field': 'type', 'header': 'Type' },
    { 'field': 'occuredDateValue', 'header': 'Date Occured' },
    { 'field': 'reportedDateValue', 'header': 'Reported Date' },
    { 'field': 'severityLevel', 'header': 'Severity Level' },
    { 'field': 'reportedBy', 'header': 'Reported By' },
    {
      'field': 'systemsAndServicesImpacted',
      'header': 'Systems & Services Impacted',
    },
    { 'field': 'initialReportingValue', 'header': 'Initial Reporting' },
    {
      'field': 'incidentSequenceOfEventsValue',
      'header': 'Incident Sequence of Events',
    },
    { 'field': 'incidentMitigationValue', 'header': 'Incident Mitigation' },
    { 'field': 'documentationValue', 'header': 'Documentation' },
    { 'field': 'recommendationsValue', 'header': 'Recommendations' },
    {
      'field': 'additionalCommentsNotesValue',
      'header': 'Additional Comments/Notes',
    },
  ];

  attachmentCols: any[] = [
    { 'field': 'date', 'header': 'Date', width: '18em' },
    { 'field': 'name', 'header': 'Name' },
  ];

  dialogRef: DynamicDialogRef | undefined;

  constructor(
    public router: Router,
    public userService: UserService,
    public incidentResponseService: IncidentResponseService,
    public authenticationService: AuthenticationService,
    public alertService: AlertService,
    public clientProfileService: ClientProfileService,
    public fileBlobService: FileBlobService,
    public dialogService: DialogService
  ) {
    const tenantName = LoginHelper.getTenantName();
    if (tenantName == null || LoginHelper.getUserToken() == null) {
      this.authenticationService.logout();
      return;
    }
    this.clientProfileService.getPublicSettings(LoginHelper.getTenantName()).subscribe(s => this.pageTitle = s.menuLabels['IR'] || 'Incident Response');
  }

  ngOnInit(): void {
    forkJoin([
      this.incidentResponseService.getIncidentResponses(),
      this.userService.getAll()
    ])
    .subscribe((data) => {
      this.incidentResponses = data[0];
      this.incidentResponses.forEach((r) => {
        (<any>r).occuredDateValue = moment(r.occuredDate).format('l');
        (<any>r).reportedDateValue = moment(r.reportedDate).format('l');
        (<any>r).initialReportingValue = (r.initialReporting || '').replace(
          /<.*?>/g,
          ''
        );
        (<any>r).incidentSequenceOfEventsValue = (
          r.incidentSequenceOfEvents || ''
        ).replace(/<.*?>/g, '');
        (<any>r).incidentMitigationValue = (r.incidentMitigation || '').replace(
          /<.*?>/g,
          ''
        );
        (<any>r).documentationValue = (r.documentation || '').replace(
          /<.*?>/g,
          ''
        );
        (<any>r).recommendationsValue = (r.recommendations || '').replace(
          /<.*?>/g,
          ''
        );
        (<any>r).additionalCommentsNotesValue = (
          r.additionalCommentsNotes || ''
        ).replace(/<.*?>/g, '');
        (<any>r).approver1Name = r.approver1Id ? data[1].find(u => u.userId == r.approver1Id)?.fullName : 0;
        (<any>r).approver2Name = r.approver2Id ? data[1].find(u => u.userId == r.approver2Id)?.fullName : 0;
      });
    });
  }

  edit(item = null) {
    const header = `${
      item?.incidentResponseId ? 'Edit' : 'Create'
    } Request List Item${
      item?.incidentResponseId ? ' - ' + item?.incidentResponseNumber : ''
    }`;

    this.dialogRef = this.dialogService.open(EditIncidentResponseComponent, {
      width: '80%',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: true,
      data: { incidentResponse: item },
      header,
    });

    this.dialogRef.onClose.subscribe(() => {
      this.ngOnInit();
    });
  }

  dropIncidentResponse(item) {
    this.incidentResponseService.deleteIncidentResponse(
      item.incidentResponseId
    ).subscribe(() => {
      this.ngOnInit();
    });
  }

  addToken(html) {
    return html
      ? html.replace(
          /api\/attachments\/file\?/gi,
          'api/attachments/file?token=' +
            LoginHelper.getFileToken() +
            '&'
        )
      : html;
  }

  public DownloadAttachment(fileName: string, isPath: boolean = false) {
    this.fileBlobService.downloadFile(fileName, 'IncidentResponse').subscribe(
      (blob) => {
        importedSaveAs(new Blob([blob]), fileName);
      },
      (error) => {
        const errorMessage =
          error.status == 401
            ? "The logged in user does not have access to the file's location. "
            : 'The referenced file no longer exists in the document library.';
        this.alertService.error(errorMessage);
      }
    );
  }

  ngOnDestroy() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
